import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { Package, Save, Trash2, Plus, Loader2, CheckCircle2, CircleDashed, Clock, Tag, Layers, History, AlertTriangle, X, Languages, Check } from 'lucide-react';
import { toast } from 'sonner';
import { MODULE_ICON_OPTIONS, getModuleIcon, MODULE_LANGS, moduleText } from '@/lib/moduleCatalog';

const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS_LABEL = { active: 'Aktiv', pending: 'Ausstehend', canceling: 'Kündigt', canceled: 'Beendet', none: 'Kein Abo' };
const HIST_LABEL = { base: 'Standardpreis', tier: 'Mengenstaffel', tenant: 'Mandanten-Sonderpreis' };
const money = (n) => (Number(n) || 0).toFixed(2);
const ovText = (o) => {
  if (!o || typeof o !== 'object') return '—';
  if (o.mode === 'fixed') return `Sonderpreis CHF ${money(o.monthly_chf)}/Mt · ${money(o.yearly_chf)}/J`;
  if (o.mode === 'percent') return `Dauerrabatt ${money(o.percent)}%`;
  return 'Standardpreis';
};
function histDescribe(h) {
  if (h.type === 'base') {
    return `CHF ${money(h.old?.monthly_chf)}/${money(h.old?.yearly_chf)} → CHF ${money(h.new?.monthly_chf)}/${money(h.new?.yearly_chf)} (Monat/Jahr)`;
  }
  if (h.type === 'tier') {
    const fmt = (arr) => (arr || []).length ? (arr || []).map((t) => `ab ${t.min_qty}:${money(t.percent_off)}%`).join(', ') : 'keine';
    return `Staffel: ${fmt(h.old)} → ${fmt(h.new)}`;
  }
  if (h.type === 'tenant') {
    return `${ovText(h.old)} → ${ovText(h.new)}`;
  }
  return '';
}

export default function ModulesAdminPage() {
  const [modules, setModules] = useState([]);
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [newMod, setNewMod] = useState({ key: '', name: '', description: '', benefits: '', icon: 'package', monthly_chf: '', yearly_chf: '', active: true });
  const [trialDays, setTrialDays] = useState(14);
  const [studioLic, setStudioLic] = useState({ monthly_chf: '', yearly_chf: '' });
  const [tiers, setTiers] = useState([]);
  const [history, setHistory] = useState([]);
  const [impact, setImpact] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [actTpl, setActTpl] = useState(null);
  const [actBusy, setActBusy] = useState(false);
  const [recCfg, setRecCfg] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [promos, setPromos] = useState([]);
  const [newPromo, setNewPromo] = useState({ code: '', percent_off: '', amount_off_chf: '' });
  const [starterPkgs, setStarterPkgs] = useState([]);
  const [starterCatalog, setStarterCatalog] = useState({});
  const [starterBusy, setStarterBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [m, s, c, p, h] = await Promise.all([
        superAdminApi.listModules(), superAdminApi.moduleSubscriptions(),
        superAdminApi.getSubConfig(), superAdminApi.listPromos(),
        superAdminApi.studioPriceHistory(50),
      ]);
      setModules(m.data.modules || []);
      setSubs(s.data);
      setTrialDays(c.data.trial_days);
      setStudioLic({ monthly_chf: c.data.studio_license_monthly_chf ?? '', yearly_chf: c.data.studio_license_yearly_chf ?? '' });
      setTiers((c.data.studio_license_tiers || []).map((t) => ({ min_qty: t.min_qty, percent_off: t.percent_off })));
      setPromos(p.data.promo_codes || []);
      setHistory(h.data.history || []);
      superAdminApi.emailStatus().then((e) => setEmailStatus(e.data)).catch(() => setEmailStatus(null));
      superAdminApi.getActivationEmail().then((a) => setActTpl(a.data)).catch(() => setActTpl(null));
      superAdminApi.getRecommendationConfig().then((r) => setRecCfg(r.data)).catch(() => setRecCfg(null));
      superAdminApi.getStarterPackages().then((r) => { setStarterPkgs(r.data.packages || []); setStarterCatalog(r.data.catalog || {}); }).catch(() => {});
    } catch { toast.error('Daten konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveTrial = async () => {
    setSaving('trial');
    try { await superAdminApi.setSubConfig({ trial_days: Number(trialDays) || 0 }); toast.success('Testphase gespeichert'); }
    catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const sendTestEmail = async () => {
    setTestBusy(true);
    try {
      const r = await superAdminApi.emailTest(testEmail.trim());
      if (r.data.ok) toast.success(`Testnachricht gesendet an ${r.data.to}`);
      else toast.error(r.data.error || 'Testnachricht konnte nicht gesendet werden');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Testnachricht fehlgeschlagen'); }
    finally { setTestBusy(false); }
  };

  const saveActivationTpl = async () => {
    setActBusy(true);
    try {
      const r = await superAdminApi.setActivationEmail(actTpl);
      setActTpl(r.data);
      toast.success('Aktivierungs-Mail gespeichert');
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setActBusy(false); }
  };

  const toggleStarterKey = (branche, key) => setStarterPkgs((pkgs) => pkgs.map((p) => p.branche === branche
    ? { ...p, module_keys: p.module_keys.includes(key) ? p.module_keys.filter((k) => k !== key) : [...p.module_keys, key], _dirty: true }
    : p));
  const saveStarter = async (p) => {
    setStarterBusy(p.branche);
    try {
      const r = await superAdminApi.saveStarterPackage({ branche: p.branche, module_keys: p.module_keys });
      setStarterPkgs((pkgs) => pkgs.map((x) => x.branche === p.branche ? { ...r.data.package, _dirty: false } : x));
      toast.success('Startpaket gespeichert');
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setStarterBusy(''); }
  };
  const resetStarter = async (branche) => {
    setStarterBusy(branche);
    try {
      const r = await superAdminApi.resetStarterPackage(branche);
      setStarterPkgs((pkgs) => pkgs.map((x) => x.branche === branche ? { ...r.data.package, _dirty: false } : x));
      toast.success('Auf Standard zurückgesetzt');
    } catch { toast.error('Zurücksetzen fehlgeschlagen'); }
    finally { setStarterBusy(''); }
  };

  const toggleRecKey = (key) => setRecCfg((c) => ({ ...c, recommendable_keys: c.recommendable_keys.includes(key) ? c.recommendable_keys.filter((k) => k !== key) : [...c.recommendable_keys, key] }));
  const saveRecCfg = async () => {
    setRecBusy(true);
    try {
      await superAdminApi.setRecommendationConfig(recCfg.recommendable_keys);
      toast.success('Empfehlungs-Einstellungen gespeichert');
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setRecBusy(false); }
  };

  const addTier = () => setTiers([...tiers, { min_qty: '', percent_off: '' }]);
  const updateTier = (i, patch) => { const c = [...tiers]; c[i] = { ...c[i], ...patch }; setTiers(c); };
  const removeTier = (i) => setTiers(tiers.filter((_, x) => x !== i));
  const tierPctFor = (qty) => {
    let best = { mq: 0, po: 0 };
    for (const t of tiers) {
      const mq = Number(t.min_qty), po = Number(t.percent_off);
      if (mq >= 1 && po > 0 && qty >= mq && mq >= best.mq) best = { mq, po };
    }
    return best.po;
  };

  const openSaveConfirm = async () => {
    try { const r = await superAdminApi.studioPriceImpact(); setImpact(r.data); }
    catch { setImpact(null); }
    setConfirmOpen(true);
  };

  const doSaveStudioLic = async () => {
    setSaving('studio-lic');
    try {
      await superAdminApi.setSubConfig({
        trial_days: Number(trialDays) || 0,
        studio_license_monthly_chf: Number(studioLic.monthly_chf) || 0,
        studio_license_yearly_chf: Number(studioLic.yearly_chf) || 0,
        studio_license_tiers: tiers
          .filter((t) => Number(t.percent_off) > 0 && Number(t.min_qty) >= 1)
          .map((t) => ({ min_qty: Number(t.min_qty), percent_off: Number(t.percent_off) })),
      });
      toast.success('Studio-Lizenz-Preise gespeichert');
      setConfirmOpen(false);
      await load();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const addPromo = async () => {
    if (!newPromo.code) { toast.message('Code erforderlich'); return; }
    setSaving('promo-new');
    try {
      await superAdminApi.savePromo({ code: newPromo.code, percent_off: Number(newPromo.percent_off) || 0, amount_off_chf: Number(newPromo.amount_off_chf) || 0, active: true });
      toast.success('Rabatt-Code erstellt');
      setNewPromo({ code: '', percent_off: '', amount_off_chf: '' });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erstellen fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const togglePromo = async (p) => {
    setSaving('promo-' + p.code);
    try { await superAdminApi.savePromo({ code: p.code, percent_off: p.percent_off, amount_off_chf: p.amount_off_chf, active: !p.active }); await load(); }
    catch { toast.error('Aktion fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const removePromo = async (code) => {
    setSaving('promo-del-' + code);
    try { await superAdminApi.deletePromo(code); toast.success('Rabatt-Code deaktiviert'); await load(); }
    catch { toast.error('Fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const saveMod = async (mod) => {
    setSaving('mod-' + mod.key);
    try {
      await superAdminApi.saveModule({
        key: mod.key, name: mod.name, description: mod.description || '',
        benefits: (mod.benefits || []).map((b) => String(b).trim()).filter(Boolean),
        translations: mod.translations || null, icon: mod.icon || 'package',
        monthly_chf: Number(mod.monthly_chf) || 0, yearly_chf: Number(mod.yearly_chf) || 0, active: !!mod.active,
      });
      toast.success(`Modul ${mod.name} gespeichert`);
      await load();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const addMod = async () => {
    if (!newMod.key || !newMod.name) { toast.message('Schlüssel und Name erforderlich'); return; }
    setSaving('new');
    try {
      await superAdminApi.saveModule({
        ...newMod,
        benefits: String(newMod.benefits || '').split('\n').map((b) => b.trim()).filter(Boolean),
        icon: newMod.icon || 'package',
        monthly_chf: Number(newMod.monthly_chf) || 0, yearly_chf: Number(newMod.yearly_chf) || 0,
      });
      toast.success('Modul hinzugefügt');
      setNewMod({ key: '', name: '', description: '', benefits: '', icon: 'package', monthly_chf: '', yearly_chf: '', active: true });
      await load();
    } catch { toast.error('Hinzufügen fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const removeMod = async (key) => {
    setSaving('del-' + key);
    try { await superAdminApi.deleteModule(key); toast.success('Modul entfernt'); await load(); }
    catch { toast.error('Entfernen fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const update = (i, patch) => { const c = [...modules]; c[i] = { ...c[i], ...patch }; setModules(c); };

  if (loading) return <div className="text-slate-600" data-testid="sa-modules-loading">Lädt…</div>;

  return (
    <div data-testid="sa-modules" className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Software-Abo</h1>
        <p className="mt-1 text-sm text-slate-600">Modulkatalog und Preise. Mandanten wählen Module und zahlen das Abo wiederkehrend an InvictaX.</p>
      </div>

      {/* MRR */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-3xl font-semibold text-slate-900">{chf(subs?.mrr_chf)}</div>
          <div className="text-sm text-slate-600">Wiederkehrender Umsatz / Monat (MRR)</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-3xl font-semibold text-slate-900">{(subs?.subscriptions || []).filter((s) => s.status === 'active').length}</div>
          <div className="text-sm text-slate-600">Aktive Abos</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-3xl font-semibold text-slate-900">{modules.filter((m) => m.active).length}</div>
          <div className="text-sm text-slate-600">Aktive Module</div>
        </div>
      </div>

      {/* Module */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-medium text-slate-900"><Package className="h-5 w-5 text-indigo-700" /> Module & Preise</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="pb-3">Schlüssel</th><th className="pb-3">Name</th><th className="pb-3 text-right">CHF/Monat</th><th className="pb-3 text-right">CHF/Jahr</th><th className="pb-3 text-center">Aktiv</th><th className="pb-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-200">
              {modules.map((m, i) => (
                <React.Fragment key={m.key}>
                <tr data-testid={`sa-module-row-${m.key}`}>
                  <td className="py-2 text-slate-500">{m.key}</td>
                  <td className="py-2"><input className="w-48 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={m.name} onChange={(e) => update(i, { name: e.target.value })} data-testid={`sa-module-name-${m.key}`} /></td>
                  <td className="py-2 text-right"><input type="number" className="w-20 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-right text-slate-800" value={m.monthly_chf} onChange={(e) => update(i, { monthly_chf: e.target.value })} data-testid={`sa-module-monthly-${m.key}`} /></td>
                  <td className="py-2 text-right"><input type="number" className="w-20 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-right text-slate-800" value={m.yearly_chf} onChange={(e) => update(i, { yearly_chf: e.target.value })} data-testid={`sa-module-yearly-${m.key}`} /></td>
                  <td className="py-2 text-center"><input type="checkbox" checked={!!m.active} onChange={(e) => update(i, { active: e.target.checked })} className="h-4 w-4 rounded border-slate-300 bg-slate-100 text-indigo-500" data-testid={`sa-module-active-${m.key}`} /></td>
                  <td className="py-2 text-right">
                    <button onClick={() => saveMod(modules[i])} disabled={saving === 'mod-' + m.key} className="mr-1 rounded-lg bg-indigo-500/20 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-500/30" data-testid={`sa-module-save-${m.key}`}>{saving === 'mod-' + m.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}</button>
                    <button onClick={() => removeMod(m.key)} disabled={saving === 'del-' + m.key} className="rounded-lg bg-rose-500/15 px-2 py-1 text-xs text-rose-700 hover:bg-rose-500/25" data-testid={`sa-module-del-${m.key}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
                <tr key={m.key + '-detail'} data-testid={`sa-module-detail-${m.key}`} className="bg-slate-50/60">
                  <td></td>
                  <td colSpan={5} className="py-2 pr-4">
                    <ModuleDetailEditor m={m} onChange={(patch) => update(i, patch)} />
                  </td>
                </tr>
                </React.Fragment>
              ))}
              {/* New module row */}
              <tr className="bg-slate-50">
                <td className="py-2"><input placeholder="key" className="w-24 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={newMod.key} onChange={(e) => setNewMod({ ...newMod, key: e.target.value })} data-testid="sa-module-new-key" /></td>
                <td className="py-2"><input placeholder="Name" className="w-48 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={newMod.name} onChange={(e) => setNewMod({ ...newMod, name: e.target.value })} data-testid="sa-module-new-name" /></td>
                <td className="py-2 text-right"><input type="number" placeholder="0" className="w-20 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-right text-slate-800" value={newMod.monthly_chf} onChange={(e) => setNewMod({ ...newMod, monthly_chf: e.target.value })} data-testid="sa-module-new-monthly" /></td>
                <td className="py-2 text-right"><input type="number" placeholder="0" className="w-20 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-right text-slate-800" value={newMod.yearly_chf} onChange={(e) => setNewMod({ ...newMod, yearly_chf: e.target.value })} data-testid="sa-module-new-yearly" /></td>
                <td></td>
                <td className="py-2 text-right"><button onClick={addMod} disabled={saving === 'new'} className="rounded-lg bg-emerald-500/20 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-500/30" data-testid="sa-module-add"><Plus className="h-3.5 w-3.5" /></button></td>
              </tr>
              <tr className="bg-slate-50">
                <td></td>
                <td colSpan={5} className="py-2 pr-4">
                  <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Symbol</label>
                    <IconPicker value={newMod.icon} onChange={(icon) => setNewMod({ ...newMod, icon })} testid="sa-module-new-icon" />
                  </div>
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-500">Beschreibung (für Kunden)</label>
                      <textarea rows={2} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-800" value={newMod.description} onChange={(e) => setNewMod({ ...newMod, description: e.target.value })} placeholder="Kurzer Satz, was das Modul bringt." data-testid="sa-module-new-description" />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-500">Vorteile · ein Vorteil pro Zeile</label>
                      <textarea rows={2} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-800" value={newMod.benefits} onChange={(e) => setNewMod({ ...newMod, benefits: e.target.value })} placeholder={'Online-Buchung rund um die Uhr\n…'} data-testid="sa-module-new-benefits" />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Subscriptions */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-modules-subscriptions">
        <h2 className="mb-4 text-lg font-medium text-slate-900">Abos je Mandant</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500"><th className="pb-3">Mandant</th><th className="pb-3">Status</th><th className="pb-3">Module</th><th className="pb-3">Intervall</th><th className="pb-3 text-right">Betrag</th></tr></thead>
            <tbody className="divide-y divide-slate-200">
              {(subs?.subscriptions || []).map((s) => (
                <tr key={s.slug} data-testid={`sa-sub-row-${s.slug}`}>
                  <td className="py-2 text-slate-800">{s.name || s.slug}<div className="text-xs text-slate-500">{s.slug}</div></td>
                  <td className="py-2">{s.status === 'active' ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" /> {STATUS_LABEL[s.status]}</span> : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><CircleDashed className="h-3 w-3" /> {STATUS_LABEL[s.status] || s.status}</span>}</td>
                  <td className="py-2 text-slate-600">{(s.module_keys || []).length ? s.module_keys.join(', ') : '—'}</td>
                  <td className="py-2 text-slate-600">{s.interval === 'year' ? 'Jährlich' : s.interval === 'month' ? 'Monatlich' : '—'}</td>
                  <td className="py-2 text-right text-slate-800">{s.amount_chf ? chf(s.amount_chf) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Testphase */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-trial-config">
        <h2 className="mb-1 flex items-center gap-2 text-lg font-medium text-slate-900"><Clock className="h-5 w-5 text-indigo-700" /> Gratis-Testphase</h2>
        <p className="mb-4 text-xs text-slate-500">Neue Mandanten zahlen erst nach Ablauf der Testphase. Gilt nur beim ersten Abo.</p>
        <div className="flex items-center gap-2">
          <input type="number" className="w-24 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} data-testid="sa-trial-days" />
          <span className="text-sm text-slate-600">Tage gratis</span>
          <button onClick={saveTrial} disabled={saving === 'trial'} className="ml-2 inline-flex items-center gap-1 rounded-lg bg-indigo-500/20 px-3 py-1.5 text-xs text-indigo-700 hover:bg-indigo-500/30" data-testid="sa-trial-save">{saving === 'trial' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Speichern</button>
        </div>
      </div>

      {/* Studio-Lizenz-Preise */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-studio-license-config">
        <h2 className="mb-1 flex items-center gap-2 text-lg font-medium text-slate-900"><Package className="h-5 w-5 text-slate-700" /> Studio-Lizenz (zusätzliche Standorte)</h2>
        <p className="mb-4 text-xs text-slate-500">Preis pro zusätzlichem Studio, das ein Mandant über sein Grundkontingent hinaus freischaltet.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Monatlich (CHF)</label>
            <input type="number" step="0.01" min="0" className="w-32 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={studioLic.monthly_chf} onChange={(e) => setStudioLic((p) => ({ ...p, monthly_chf: e.target.value }))} data-testid="sa-studio-license-monthly" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Jährlich (CHF)</label>
            <input type="number" step="0.01" min="0" className="w-32 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={studioLic.yearly_chf} onChange={(e) => setStudioLic((p) => ({ ...p, yearly_chf: e.target.value }))} data-testid="sa-studio-license-yearly" />
          </div>
        </div>

        {/* Mengenstaffel */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="sa-studio-tiers">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-slate-900"><Layers className="h-4 w-4 text-slate-700" /> Mengenstaffel</div>
          <p className="mb-3 text-xs text-slate-500">Rabatt pro zusätzlicher Studio-Lizenz: Ab der angegebenen Lizenz-Nummer gilt der Prozent-Rabatt auf den Standardpreis. Beispiel: „ab 3 → 10%" macht die 3., 4. … Zusatzlizenz 10% günstiger.</p>
          <div className="space-y-2">
            {tiers.length === 0 && <div className="text-xs text-slate-400" data-testid="sa-studio-tiers-empty">Keine Staffel — jede Zusatzlizenz kostet den Standardpreis.</div>}
            {tiers.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`sa-studio-tier-row-${i}`}>
                <span className="text-xs text-slate-500">ab Lizenz-Nr.</span>
                <input type="number" min="1" className="w-20 rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-800" value={t.min_qty} onChange={(e) => updateTier(i, { min_qty: e.target.value })} data-testid={`sa-studio-tier-minqty-${i}`} />
                <span className="text-xs text-slate-500">Rabatt</span>
                <input type="number" min="0" max="100" className="w-20 rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-800" value={t.percent_off} onChange={(e) => updateTier(i, { percent_off: e.target.value })} data-testid={`sa-studio-tier-percent-${i}`} />
                <span className="text-xs text-slate-500">%</span>
                <button onClick={() => removeTier(i)} className="rounded-lg bg-rose-500/15 px-2 py-1 text-rose-700 hover:bg-rose-500/25" data-testid={`sa-studio-tier-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <button onClick={addTier} className="mt-3 inline-flex items-center gap-1 rounded-lg bg-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-300" data-testid="sa-studio-tier-add"><Plus className="h-3.5 w-3.5" /> Staffel-Stufe</button>

          {/* Beispielrechnung */}
          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3" data-testid="sa-studio-tier-preview">
            <div className="mb-2 text-xs font-medium text-slate-700">Beispielrechnung je Zusatzlizenz</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-400">
                    <th className="py-1 pr-3 font-medium">Zusatzlizenz</th>
                    <th className="py-1 pr-3 font-medium">Rabatt</th>
                    <th className="py-1 pr-3 font-medium">Monatlich</th>
                    <th className="py-1 font-medium">Jährlich</th>
                  </tr>
                </thead>
                <tbody>
                  {[2, 3, 5].map((q) => {
                    const pct = tierPctFor(q);
                    const m = (Number(studioLic.monthly_chf) || 0) * (1 - pct / 100);
                    const y = (Number(studioLic.yearly_chf) || 0) * (1 - pct / 100);
                    return (
                      <tr key={q} className="border-t border-slate-100 text-slate-700" data-testid={`sa-studio-tier-preview-row-${q}`}>
                        <td className="py-1 pr-3">{q}. Zusatzlizenz</td>
                        <td className="py-1 pr-3">{pct > 0 ? `−${money(pct)}%` : '—'}</td>
                        <td className="py-1 pr-3 font-medium">{chf(m)}</td>
                        <td className="py-1 font-medium">{chf(y)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Vorschau auf Basis der aktuellen Eingaben — noch nicht gespeichert. Die 1. Zusatzlizenz entspricht dem Standardpreis.</p>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={openSaveConfirm} disabled={saving === 'studio-lic'} className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50" data-testid="sa-studio-license-save">{saving === 'studio-lic' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Preise & Staffel speichern</button>
        </div>
      </div>

      {/* Preishistorie */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-studio-price-history">
        <h2 className="mb-1 flex items-center gap-2 text-lg font-medium text-slate-900"><History className="h-5 w-5 text-slate-700" /> Preishistorie Studio-Lizenz</h2>
        <p className="mb-3 text-xs text-slate-500">Wann welcher Preis, welche Staffel oder welcher Mandanten-Sonderpreis geändert wurde.</p>
        {emailStatus && (
          <div className={`mb-4 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium ${emailStatus.configured ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid="sa-email-status">
            {emailStatus.configured ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {emailStatus.configured
              ? <>E-Mail-Versand aktiv — Preisänderungen werden automatisch an Mandanten gesendet{emailStatus.from_email ? ` (${emailStatus.from_email})` : ''}.</>
              : <>E-Mail-Versand nicht konfiguriert — Preisänderungs-Benachrichtigungen werden nicht zugestellt. Plattform-SMTP in den Einstellungen hinterlegen.</>}
          </div>
        )}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="sa-email-test">
          <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Test-E-Mail-Adresse" className="w-56 rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-800" data-testid="sa-email-test-input" />
          <button onClick={sendTestEmail} disabled={testBusy} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50" data-testid="sa-email-test-send">
            {testBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />} Testnachricht senden
          </button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="sa-history-empty">Noch keine Änderungen aufgezeichnet.</p>
        ) : (
          <div className="space-y-2" data-testid="sa-history-list">
            {history.map((h) => (
              <div key={h.id} className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs" data-testid={`sa-history-item-${h.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-700">{HIST_LABEL[h.type] || h.type}</span>
                    {h.tenant_slug && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-slate-700">{h.tenant_slug}</span>}
                  </div>
                  <div className="mt-1 text-slate-600">{histDescribe(h)}</div>
                  <div className="text-[11px] text-slate-400">von {h.changed_by}</div>
                </div>
                <div className="shrink-0 text-right text-[11px] text-slate-400">{new Date(h.changed_at).toLocaleString('de-CH')}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aktivierungs-Mail-Vorlage (zentral) */}
      {actTpl && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-activation-email">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-medium text-slate-900"><Package className="h-5 w-5 text-slate-700" /> Aktivierungs-Mail (zentrales InvictaX-Branding)</h2>
          <p className="mb-4 text-xs text-slate-500">Diese E-Mail erhalten eure Mandanten beim Studio-Paketkauf. Logo, Absendername und Textbausteine gelten für alle Mandanten.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs text-slate-500">Logo-URL</label>
              <input className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-800" value={actTpl.logo_url} onChange={(e) => setActTpl({ ...actTpl, logo_url: e.target.value })} placeholder="https://…/logo.png" data-testid="sa-activation-logo" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Absendername</label>
              <input className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-800" value={actTpl.sender_name} onChange={(e) => setActTpl({ ...actTpl, sender_name: e.target.value })} data-testid="sa-activation-sender" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Betreff</label>
              <input className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-800" value={actTpl.subject} onChange={(e) => setActTpl({ ...actTpl, subject: e.target.value })} data-testid="sa-activation-subject" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs text-slate-500">Einleitungstext</label>
              <textarea rows={2} className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-800" value={actTpl.intro} onChange={(e) => setActTpl({ ...actTpl, intro: e.target.value })} data-testid="sa-activation-intro" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs text-slate-500">Schlusstext</label>
              <textarea rows={2} className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-800" value={actTpl.outro} onChange={(e) => setActTpl({ ...actTpl, outro: e.target.value })} data-testid="sa-activation-outro" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={saveActivationTpl} disabled={actBusy} className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50" data-testid="sa-activation-save">{actBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Vorlage speichern</button>
          </div>
        </div>
      )}

      {/* Branchen-Startpakete */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-starter-packages">
        <h2 className="mb-1 flex items-center gap-2 text-lg font-medium text-slate-900"><Package className="h-5 w-5 text-indigo-600" /> Branchen-Startpakete</h2>
        <p className="mb-4 text-xs text-slate-500">Lege pro Branche fest, welche Module bei der Einrichtung vorausgewählt werden. Die Branchenwahl setzt zudem den Praxistyp des Mandanten. Gesperrte Module werden je Branche automatisch ausgeblendet.</p>
        <div className="space-y-2">
          {starterPkgs.map((p) => {
            const entries = Object.entries(starterCatalog).filter(([, m]) => m.customer_visible && !(m.blocked_industries || []).includes(p.practice_type));
            return (
              <details key={p.branche} className="group rounded-xl border border-slate-200 bg-slate-50" data-testid={`sa-starter-${p.branche}`}>
                <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3">
                  <span className="font-medium text-slate-800">{p.label}</span>
                  <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-600">{p.practice_type}</span>
                  {p.customized && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700" data-testid={`sa-starter-custom-${p.branche}`}>angepasst</span>}
                  <span className="ml-auto text-xs text-slate-500" data-testid={`sa-starter-summary-${p.branche}`}>{p.module_keys.length} Module · {chf(p.price_chf)}/Mt</span>
                </summary>
                <div className="border-t border-slate-200 px-4 py-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {entries.map(([k, m]) => (
                      <label key={k} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800" data-testid={`sa-starter-${p.branche}-mod-${k}`}>
                        <input type="checkbox" checked={p.module_keys.includes(k)} onChange={() => toggleStarterKey(p.branche, k)} data-testid={`sa-starter-${p.branche}-cb-${k}`} />
                        <span className="flex-1">{m.name}</span>
                        <span className="text-xs text-slate-400">{chf(m.price_chf)}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button onClick={() => resetStarter(p.branche)} disabled={starterBusy === p.branche || !p.customized} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-200 disabled:opacity-40" data-testid={`sa-starter-reset-${p.branche}`}>Auf Standard</button>
                    <button onClick={() => saveStarter(p)} disabled={starterBusy === p.branche} className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-indigo-400 disabled:opacity-50" data-testid={`sa-starter-save-${p.branche}`}>{starterBusy === p.branche ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Speichern</button>
                  </div>
                </div>
              </details>
            );
          })}
        </div>
      </div>

      {/* Empfehlbare Module (Allowlist) */}
      {recCfg && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-recommendation-config">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-medium text-slate-900"><Layers className="h-5 w-5 text-slate-700" /> Empfehlbare Module</h2>
          <p className="mb-4 text-xs text-slate-500">Lege fest, welche Module dem Kunden beim Studio-Paketkauf überhaupt vorgeschlagen werden dürfen. Nicht angehakte Module werden nie empfohlen.</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(recCfg.modules || []).map((m) => (
              <label key={m.key} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800" data-testid={`sa-rec-module-${m.key}`}>
                <input type="checkbox" checked={recCfg.recommendable_keys.includes(m.key)} onChange={() => toggleRecKey(m.key)} data-testid={`sa-rec-module-cb-${m.key}`} />
                {m.name}
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={saveRecCfg} disabled={recBusy} className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50" data-testid="sa-rec-save">{recBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern</button>
          </div>
        </div>
      )}

      {/* Bestätigung bei Preisänderung */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setConfirmOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="sa-price-confirm-dialog">
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <h3 className="text-lg font-semibold text-slate-900">Preisänderung bestätigen</h3>
              <button onClick={() => setConfirmOpen(false)} className="ml-auto rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            {impact ? (
              <p className="text-sm text-slate-600" data-testid="sa-price-impact-text">
                Diese Änderung betrifft <span className="font-semibold text-slate-900">{impact.affected}</span> von {impact.active_tenants} aktiven Mandanten.
                {impact.with_fixed_price > 0 && <> {impact.with_fixed_price} Mandant{impact.with_fixed_price === 1 ? '' : 'en'} mit festem Sonderpreis {impact.with_fixed_price === 1 ? 'ist' : 'sind'} nicht betroffen.</>}
              </p>
            ) : (
              <p className="text-sm text-slate-500">Die Anzahl betroffener Mandanten konnte nicht geladen werden.</p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} className="rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700 hover:bg-slate-200" data-testid="sa-price-confirm-cancel">Abbrechen</button>
              <button onClick={doSaveStudioLic} disabled={saving === 'studio-lic'} className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50" data-testid="sa-price-confirm-save">{saving === 'studio-lic' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern</button>
            </div>
          </div>
        </div>
      )}

      {/* Rabatt-Codes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-promos">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-medium text-slate-900"><Tag className="h-5 w-5 text-emerald-700" /> Rabatt-Codes</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500"><th className="pb-3">Code</th><th className="pb-3 text-right">Rabatt</th><th className="pb-3 text-right">Eingelöst</th><th className="pb-3 text-right">Umsatz</th><th className="pb-3 text-center">Aktiv</th><th className="pb-3"></th></tr></thead>
            <tbody className="divide-y divide-slate-200">
              {promos.map((p) => (
                <tr key={p.code} data-testid={`sa-promo-row-${p.code}`}>
                  <td className="py-2 font-medium text-slate-800">{p.code}</td>
                  <td className="py-2 text-right text-slate-700">{p.percent_off ? `${p.percent_off}%` : chf(p.amount_off_chf)}</td>
                  <td className="py-2 text-right text-slate-700" data-testid={`sa-promo-redemptions-${p.code}`}>{p.redemptions || 0}×</td>
                  <td className="py-2 text-right text-emerald-700" data-testid={`sa-promo-revenue-${p.code}`}>{chf(p.revenue_chf || 0)}</td>
                  <td className="py-2 text-center">{p.active ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700">Aktiv</span> : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Inaktiv</span>}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => togglePromo(p)} disabled={saving === 'promo-' + p.code} className="mr-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-200" data-testid={`sa-promo-toggle-${p.code}`}>{p.active ? 'Deaktivieren' : 'Aktivieren'}</button>
                    <button onClick={() => removePromo(p.code)} disabled={saving === 'promo-del-' + p.code} className="rounded-lg bg-rose-500/15 px-2 py-1 text-xs text-rose-700 hover:bg-rose-500/25" data-testid={`sa-promo-del-${p.code}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50">
                <td className="py-2"><input placeholder="CODE" className="w-28 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 uppercase text-slate-800" value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value })} data-testid="sa-promo-new-code" /></td>
                <td className="py-2 text-right"><div className="flex items-center justify-end gap-1"><input type="number" placeholder="%" className="w-16 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-right text-slate-800" value={newPromo.percent_off} onChange={(e) => setNewPromo({ ...newPromo, percent_off: e.target.value })} data-testid="sa-promo-new-percent" /><span className="text-xs text-slate-500">oder</span><input type="number" placeholder="CHF" className="w-16 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-right text-slate-800" value={newPromo.amount_off_chf} onChange={(e) => setNewPromo({ ...newPromo, amount_off_chf: e.target.value })} data-testid="sa-promo-new-amount" /></div></td>
                <td></td>
                <td></td>
                <td></td>
                <td className="py-2 text-right"><button onClick={addPromo} disabled={saving === 'promo-new'} className="rounded-lg bg-emerald-500/20 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-500/30" data-testid="sa-promo-add"><Plus className="h-3.5 w-3.5" /></button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500">Rabatt gilt dauerhaft auf den Abopreis. Zum Ändern eines Betrags: Code löschen und neu anlegen.</p>
      </div>
    </div>
  );
}

function IconPicker({ value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {MODULE_ICON_OPTIONS.map((key) => {
        const Ico = getModuleIcon(key);
        const active = (value || 'package') === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            title={key}
            data-testid={`${testid}-${key}`}
            className={`flex h-8 w-8 items-center justify-center rounded-lg border transition ${active ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-200' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}
          >
            <Ico className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

function ModuleDetailEditor({ m, onChange }) {
  const [translating, setTranslating] = useState(false);
  const [prevLang, setPrevLang] = useState('de');
  const Ico = getModuleIcon(m.icon);
  const hasTranslations = !!(m.translations && (m.translations.fr || m.translations.it || m.translations.en));
  const preview = moduleText(m, prevLang);

  const runTranslate = async () => {
    setTranslating(true);
    try {
      const benefits = (m.benefits || []).map((b) => String(b).trim()).filter(Boolean);
      const r = await superAdminApi.translateModule({ description: m.description || '', benefits });
      onChange({
        description: r.data.description_de ?? m.description,
        benefits: Array.isArray(r.data.benefits_de) && r.data.benefits_de.length ? r.data.benefits_de : m.benefits,
        translations: r.data.translations || m.translations,
      });
      toast.success('Deutsch korrigiert · FR/IT/EN übersetzt. Zum Übernehmen „Speichern" klicken.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Übersetzung fehlgeschlagen');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Editor */}
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Symbol</label>
          <IconPicker value={m.icon} onChange={(icon) => onChange({ icon })} testid={`sa-module-icon-${m.key}`} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Beschreibung (Deutsch)</label>
          <textarea rows={3} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-800" value={m.description || ''} onChange={(e) => onChange({ description: e.target.value })} placeholder="Kurzer Satz, was das Modul bringt." data-testid={`sa-module-description-${m.key}`} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Vorteile · ein Vorteil pro Zeile (Deutsch)</label>
          <textarea rows={4} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-800" value={(m.benefits || []).join('\n')} onChange={(e) => onChange({ benefits: e.target.value.split('\n') })} placeholder={'Online-Buchung rund um die Uhr\nAutomatische Erinnerungen\n…'} data-testid={`sa-module-benefits-${m.key}`} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={runTranslate} disabled={translating} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-500/15 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-500/25 disabled:opacity-50" data-testid={`sa-module-translate-${m.key}`}>
            {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
            Deutsch korrigieren & auf FR/IT/EN übersetzen
          </button>
          {hasTranslations && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700" data-testid={`sa-module-translated-badge-${m.key}`}>
              <Check className="h-3 w-3" /> FR · IT · EN vorhanden
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-400">Der Übersetzer füllt die Vorschau rechts. Danach „Speichern", damit Kunden die Sprachen sehen.</p>
      </div>

      {/* Live-Vorschau */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`sa-module-preview-${m.key}`}>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Kunden-Vorschau</span>
          <div className="flex gap-1">
            {MODULE_LANGS.map((l) => (
              <button key={l.code} type="button" onClick={() => setPrevLang(l.code)} className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${prevLang === l.code ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`sa-module-preview-lang-${m.key}-${l.code}`}>{l.label}</button>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"><Ico className="h-5 w-5" /></span>
            <span className="font-semibold text-slate-900">{m.name}</span>
          </div>
          {preview.description && <p className="mt-2 text-sm text-slate-600" data-testid={`sa-module-preview-desc-${m.key}`}>{preview.description}</p>}
          <ul className="mt-3 space-y-1.5" data-testid={`sa-module-preview-benefits-${m.key}`}>
            {preview.benefits.length === 0 && <li className="text-xs text-slate-400">Noch keine Vorteile erfasst.</li>}
            {preview.benefits.map((b, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
